import { memo, useMemo } from 'react';
import Highlighter from 'react-highlight-words';
import { isString } from 'lodash-es';
import styled from '@emotion/styled';
import {
  stickerPositionMap,
  stickers,
  stickersImage,
} from 'views/Conversation/Chat/components/MessageEditor/Toolbar/KwaiStickerPicker';
import { isURL } from 'utils/is';
import { Tooltip } from 'antd';
import { useTranslate } from '@tolgee/react';

const Link = styled.a`
  text-decoration: underline;
  &:hover {
    text-decoration: underline;
  }
`;

const Sticker = styled.span<{ x: number; y: number }>`
  display: inline-block;
  background-image: url('${stickersImage}');
  background-size: 326px 325px !important;
  background-position: ${({ x, y }) => `${x}px ${y}px`};
  width: 25px !important;
  height: 25px !important;
  margin-top: -3px !important;
  vertical-align: middle;
  transform: scale(0.75);
`;

export interface EnhanceTextProps {
  text?: string | React.ReactNode;
  platform?: General.PlatformUpperCaseTunnel;
}

const regexp =
  // eslint-disable-next-line
  /(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()'@:%_\+.~#?!&//=]*)/gi;

export interface HighlightTagProps {
  children: string;
}
const HighlightTag = memo((props: HighlightTagProps) => {
  const { children } = props;

  if (children.startsWith('[')) {
    const stickers = children
      .split(']')
      .slice(0, -1)
      .map((_) => `${_}]`);
    return (
      <>
        {stickers.map((sticker) => {
          const position = stickerPositionMap[sticker as keyof typeof stickerPositionMap];
          if (!position) {
            return sticker;
          }
          return <Sticker key={sticker} x={position[0]} y={position[1]} />;
        })}
      </>
    );
  }

  return (
    <Link href={isURL(children) ? children : `https://${children}`} target="__blank">
      {children}
    </Link>
  );
});

const EnhanceText = (props: EnhanceTextProps) => {
  const { text, platform } = props;
  const searchWords = useMemo(() => {
    let urls;
    if (text && isString(text)) {
      urls = text.match(regexp);
    }
    if (platform && platform !== 'KWAI') {
      return urls ?? [];
    }
    return urls ? [...urls, ...stickers] : stickers;
  }, [text, platform]);

  if (isString(text)) {
    return (
      <Highlighter
        autoEscape
        textToHighlight={text}
        searchWords={searchWords}
        highlightTag={HighlightTag}
      />
    );
  }

  return <>{text}</>;
};

export const SensitiveWorldEnhanceText = ({
  text,
  censorMask,
  platform,
}: {
  text: string | React.ReactNode;
  censorMask: string;
  platform: General.PlatformUpperCaseTunnel;
}) => {
  const { merchantSensitiveWord, leChatSensitiveWord } = useMemo<{
    merchantSensitiveWord: string[];
    leChatSensitiveWord: string[];
  }>(() => {
    try {
      const { merchantSensitiveWord, leChatSensitiveWord } = JSON.parse(censorMask);

      return {
        merchantSensitiveWord:
          merchantSensitiveWord?.map(({ sensitiveWord }: any) => sensitiveWord) || [],
        leChatSensitiveWord:
          leChatSensitiveWord?.map(({ sensitiveWord }: any) => sensitiveWord) || [],
      };
    } catch (_) {
      return {
        merchantSensitiveWord: [],
        leChatSensitiveWord: [],
      };
    }
  }, [censorMask]);

  const HighlightTag = useMemo(() => {
    return memo(({ children }: HighlightTagProps) => {
      const { t } = useTranslate();
      if (merchantSensitiveWord.includes(children.toLowerCase())) {
        return (
          <Tooltip title={t('店铺自定义敏感词')}>
            <span style={{ background: '#e4d9a6', cursor: 'pointer' }}>{children}</span>
          </Tooltip>
        );
      }
      if (leChatSensitiveWord.includes(children.toLowerCase())) {
        return (
          <Tooltip title={t('乐聊敏感词')}>
            <span style={{ background: '#e4d9a6', cursor: 'pointer' }}>{children}</span>
          </Tooltip>
        );
      }
      return <>{children}</>;
    });
  }, [merchantSensitiveWord, leChatSensitiveWord]);

  if (!merchantSensitiveWord.length && !leChatSensitiveWord.length) {
    return <EnhanceText text={text} platform={platform} />;
  }

  if (isString(text)) {
    return (
      <Highlighter
        autoEscape
        caseSensitive={false}
        textToHighlight={text}
        searchWords={[...merchantSensitiveWord, ...leChatSensitiveWord]}
        highlightTag={HighlightTag}
      />
    );
  }

  return <div>{text}</div>;
};

export default EnhanceText;
